import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, getUTM } from "@/lib/api";
import { CheckCircle2, Send } from "lucide-react";

const SERVICES = [
  "Family Medicine",
  "Vitality Health Check",
  "Child Vaccinations",
  "Chronic Disease Management",
  "Wound Care",
  "Family Planning",
  "Other / Not sure",
];

export default function LeadForm() {
  const [state, setState] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (v) => setState((s) => ({ ...s, [k]: typeof v === "string" ? v : v.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!state.name || !state.phone) { toast.error("Name and phone are required."); return; }
    setLoading(true);

    const utm = await getUTM();
    let ok = false;
    let lastErr = null;
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await api.post(
          "/leads",
          { ...state, source: utm.source || "landing-form", campaign: utm.campaign, medium: utm.medium },
          { timeout: 25000 }
        );
        ok = true;
        break;
      } catch (err) {
        lastErr = err;
        console.warn(`Lead submit attempt ${attempt}/3 failed`, err?.response?.status, err?.message);
        if (attempt < 3) await new Promise((r) => setTimeout(r, 800 * attempt));
      }
    }
    setLoading(false);
    if (ok) {
      setDone(true);
      toast.success("Thank you — we'll reach out shortly.");
    } else {
      console.error("Lead failed after retries:", lastErr);
      toast.error("We couldn't reach our system right now — please call 021 224 0204 or WhatsApp 072 399 4711.");
    }
  };

  if (done) {
    return (
      <div className="bg-sage/40 border border-sage rounded-xl p-8 text-center" data-testid="lead-success">
        <CheckCircle2 className="w-10 h-10 text-forest mx-auto" />
        <h3 className="font-display text-2xl text-forest mt-3">Got it, {state.name.split(" ")[0]}.</h3>
        <p className="text-ink/80 mt-2 text-sm">Our receptionist will call <span className="font-medium">{state.phone}</span> within one business day.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="lead-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-widest text-ink/70">Your name</Label>
          <Input value={state.name} onChange={set("name")} className="mt-1 bg-cream border-border" placeholder="Full name" data-testid="lead-name" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-ink/70">Phone</Label>
          <Input value={state.phone} onChange={set("phone")} className="mt-1 bg-cream border-border" placeholder="082 123 4567" data-testid="lead-phone" />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-ink/70">Email (optional)</Label>
        <Input value={state.email} onChange={set("email")} type="email" className="mt-1 bg-cream border-border" placeholder="you@example.com" data-testid="lead-email" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-ink/70">What do you need?</Label>
        <Select value={state.service} onValueChange={set("service")}>
          <SelectTrigger className="mt-1 bg-cream border-border" data-testid="lead-service">
            <SelectValue placeholder="Choose a service" />
          </SelectTrigger>
          <SelectContent>
            {SERVICES.map((s) => <SelectItem key={s} value={s} data-testid={`lead-service-${s.split(" ")[0].toLowerCase()}`}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-ink/70">Message (optional)</Label>
        <Textarea value={state.message} onChange={set("message")} className="mt-1 bg-cream border-border" rows={3} placeholder="Tell us briefly what's going on..." data-testid="lead-message" />
      </div>
      <Button type="submit" disabled={loading} className="w-full bg-clay hover:bg-clay-dark text-cream h-11" data-testid="lead-submit">
        <Send className="w-4 h-4 mr-2" />
        {loading ? "Sending..." : "Request appointment"}
      </Button>
    </form>
  );
}
